<template>
  <sp-menu
    v-if="i18nReady"
    class="availability-filter"
    :class="classModifiers"
    :label="label"
    location="bottom-left"
    close-on-content-click="false"
    :open="menuOpen"
    :disabled="disabled"
    @update-open="handleMenuUpdateOpen"
  >
    <FilterActivatorSlot
      :label="label"
      :text="summaryText"
      :prepend-icon="prependIcon"
      :open="menuOpen"
      :non-clearable="isNonClearable"
      :disabled="disabled"
      @clear="handleClear"
    >
      <template v-if="$slots.activator" #activator="activatorProps">
        <slot name="activator" v-bind="activatorProps" />
      </template>
    </FilterActivatorSlot>

    <AvailabilityPanel
      :disabled="disabled"
      :model="filterModel"
      :label="label"
      :visit-type-nodes="visitTypeNodes"
      :visit-types-loading="visitTypesLoading"
      :owns-visit-types="ownsVisitTypes"
      :show-online-booking-option="showOnlineBookingOption"
      :day-states="dayStates"
      :indicators="indicatorsEnabled"
      :today-link-href="todayLinkHref"
      @change="handlePanelChange"
      @visible-range="handleVisibleRange"
    />
  </sp-menu>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { firstDetail } from "#ce/utils/events";
import { toBoolean, toJSON, trimToUndefined } from "#ce/utils/props";
import { useForm } from "#ce/composables/useForm";
import { useControllableOpen } from "#ce/shared/filters/composables/useControllableOpen";
import { useFinderOptionScope } from "#ce/composables/useFinderOptionScope";
import { FINDER_NOT_FOUND } from "#ce/shared/finder-association";
import { useVisitTypeFilter } from "#ce/shared/filters/visit-type/composables/useVisitTypeFilter";
import {
  VISIT_TYPES_FILTER_NAME,
  useAvailabilityFilterModel,
} from "#ce/shared/filters/availability/composables/useAvailabilityFilterModel";
import { useAvailabilityFilterSummary } from "#ce/shared/filters/availability/composables/useAvailabilityFilterSummary";
import { useAvailabilityIndicators } from "#ce/shared/filters/availability/composables/useAvailabilityIndicators";
import { i18n, t, whenI18nReady } from "#ce/shared/filters/availability/i18n";
import { props as availabilityFilterProps } from "#ce/shared/filters/availability/components/props";
import FilterActivatorSlot from "#ce/shared/filters/components/FilterActivatorSlot.ce.vue";
import AvailabilityPanel from "#ce/shared/filters/availability/components/AvailabilityPanel.ce.vue";

const emit = defineEmits(["update:model-value", "update:open"]);

const props = defineProps(availabilityFilterProps);

const ownsVisitTypes = computed(() => !toBoolean(props.noVisitTypes));
const showOnlineBookingOption = computed(() => !toBoolean(props.hideOnlineBookingOption));
const isNonClearable = computed(() => toBoolean(props.nonClearable));

const label = computed(() => t("label"));

// First paint waits for the catalog, so no label flashes its English fallback. `whenI18nReady`
// settles on a failed fetch too, so the gate cannot hang.
const i18nReady = ref(false);

function markI18nReady() {
  i18nReady.value = true;
}

whenI18nReady.then(markI18nReady);

const filterModel = useAvailabilityFilterModel({
  ownsVisitTypes,
  requireSelection: () => !showOnlineBookingOption.value,
});

const {
  onlineBookingOnly,
  visitTypes,
  availableTimes,
  availableDate,
  availableDays,
  dateMode,
  filterNames,
  model,
  setFilterValues: applyFilterValues,
  clear: clearModel,
} = filterModel;

// The rows are fetched only while this filter owns `visit_types`.
const finderScope = useFinderOptionScope(props, {
  allowStandalone: true,
});
const visitTypeStore = useVisitTypeFilter({
  enabled: () => ownsVisitTypes.value && finderScope.enabled.value,
  url: trimToUndefined(props.visitTypesUrl),
  anyLabel: () => t("visit_type_prompt"),
  categoryLabels: computed(() => toJSON(props.categoryLabels, true) ?? {}),
});

finderScope.connect(visitTypeStore.setScope);

// The model owns the selection; the store's own selection follows it so `selectedText` and the
// scope exemption read the applied value.

function syncVisitTypeSelection(value) {
  visitTypeStore.selectedValue.value = value;
}

watch(visitTypes, syncVisitTypeSelection, { immediate: true });

const visitTypeNodes = computed(() => visitTypeStore.models.value);

/** Per-param wording, keyed by the names in `filterNames`: the selected visit type's title while this filter owns `visit_types`. */
const selectedTexts = computed(() =>
  ownsVisitTypes.value ? { [VISIT_TYPES_FILTER_NAME]: visitTypeStore.selectedText.value } : {},
);
const visitTypesLoading = computed(() => visitTypeStore.loading.value);

const { text: summaryText } = useAvailabilityFilterSummary(
  {
    onlineBookingOnly,
    visitTypeText: computed(() => (ownsVisitTypes.value ? visitTypeStore.selectedText.value : undefined)),
    availableTimes,
    availableDate,
    availableDays,
    dateMode,
  },
  t,
  { locale: () => i18n.global.locale.value },
);

const search = computed(() => {
  if (finderScope.finder.associationError.value === FINDER_NOT_FOUND) {
    return {};
  }

  return finderScope.finder.params.value;
});

const indicatorParams = computed(() => {
  const params = { availability: true, available_times: availableTimes.value };

  if (ownsVisitTypes.value) {
    params.visit_types = visitTypes.value;
  }

  return params;
});

const {
  dayStates,
  enabled: indicatorsEnabled,
  setVisibleRange,
  refresh: refreshIndicators,
} = useAvailabilityIndicators({ url: () => trimToUndefined(props.indicatorUrl), params: indicatorParams, search });

const { mirrorFormValue } = useForm();

mirrorFormValue(model);

const menuOpen = useControllableOpen(() => props.open, emit);

function handleMenuUpdateOpen(event) {
  if (toBoolean(props.disabled)) {
    return;
  }
  menuOpen.value = Boolean(firstDetail(event));
}

// Each handler reports the model it produced; a prop sync and a bridge restore mirror silently.
function emitModelValue(options) {
  emit("update:model-value", model.value, options);
}

// The visit type and the time of day feed the indicator request; the other fields do not.
const INDICATOR_FIELDS = Object.freeze(["visit-type", "time"]);

function handlePanelChange(field) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emitModelValue();

  if (INDICATOR_FIELDS.includes(field)) {
    refreshIndicators();
  }
}

function handleVisibleRange(range) {
  if (toBoolean(props.disabled)) {
    return;
  }
  setVisibleRange(range);
}

function handleClear() {
  if (toBoolean(props.disabled)) {
    return;
  }
  if (isNonClearable.value) {
    return;
  }

  clearModel();
  emitModelValue();
}

// A seed carries the URL's params. The resulting model is reported marked seeded, so the host's
// `value` reads the applied state.
function adoptSeed(value) {
  const values = typeof value === "string" ? toJSON(value, true) : value;

  if (!values || typeof values !== "object") {
    return;
  }

  applyFilterValues(values);
  emitModelValue({ seeded: true });
}

// A falsy echo is the host's clear signal and empties every selection, applied or not; the filter's
// own FormData echoing back is ignored.
function syncFromModelValue(value) {
  if (value instanceof FormData) {
    return;
  }

  if (!value) {
    clearModel();

    return;
  }

  adoptSeed(value);
}

watch(() => props.modelValue, syncFromModelValue, { immediate: true });

/**
 * Restores the params the bridge carries for this filter, leaving the ones it omits as they are.
 *
 * @param {Object} [values]
 */
function setFilterValues(values = {}) {
  applyFilterValues(values);
  emitModelValue({ seeded: true });
}

const active = computed(() => onlineBookingOnly.value);

const classModifiers = computed(() => ({ "--is-active": active.value }));

defineExpose({
  props,
  filterNames,
  setFilterValues,
  clear: handleClear,
  active,
  selectedText: summaryText,
  selectedTexts,
});
</script>

<style>
:host {
  display: block;
  box-sizing: border-box;
  width: 100%;
}
</style>

<style lang="scss" scoped>
.availability-filter {
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 20rem);
}
</style>
